import { useState, useEffect } from 'react';
import { superadminAPI } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Circle,
  RefreshCw,
  Mail,
  Users,
  CreditCard,
  Headphones,
  Key,
  ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router';

const CATEGORY_ICONS = {
  email: Mail,
  crm: Users,
  billing: CreditCard,
  support: Headphones,
  auth: Key,
};

// Map categories to their integration types
const CATEGORY_INTEGRATIONS = {
  email: ['email'],
  crm: ['hubspot'],
  billing: ['stripe', 'quickbooks'],
  support: ['jira'],
  auth: ['google_oauth', 'microsoft_oauth'],
};

const STATUS_CONFIG = {
  green: {
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    icon: CheckCircle,
    pulse: false,
  },
  yellow: {
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    icon: AlertTriangle,
    pulse: true,
  },
  red: {
    color: 'text-red-500',
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    icon: XCircle,
    pulse: true,
  },
  gray: {
    color: 'text-zinc-400',
    bg: 'bg-zinc-500/10',
    border: 'border-zinc-500/30',
    icon: Circle,
    pulse: false,
  },
};

export default function IntegrationStatusPanel({ compact = false, integrations = [], icons = {}, colors = {} }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const data = await superadminAPI.getIntegrationStatusOverview();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Failed to load integration status:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadStatus();
  };

  const getOverallStatus = () => {
    if (categories.length === 0) return 'gray';
    
    const hasRed = categories.some(c => c.status === 'red');
    const hasYellow = categories.some(c => c.status === 'yellow');
    const hasGreen = categories.some(c => c.status === 'green');
    
    if (hasRed) return 'red';
    if (hasYellow) return 'yellow';
    if (hasGreen) return 'green';
    return 'gray';
  };

  // Get enabled integrations for a category
  const getEnabledIntegrationsForCategory = (categoryId) => {
    const integrationTypes = CATEGORY_INTEGRATIONS[categoryId] || [];
    return integrations.filter(int => 
      integrationTypes.includes(int.integration_type) && int.is_enabled
    );
  };

  const formatLastChecked = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <Card data-testid="integration-status-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-8 w-8 rounded" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const overallStatus = getOverallStatus();
  const OverallIcon = STATUS_CONFIG[overallStatus].icon;

  if (compact) {
    // Compact view for dashboard widgets
    return (
      <Card data-testid="integration-status-panel-compact">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <OverallIcon className={`h-5 w-5 ${STATUS_CONFIG[overallStatus].color}`} />
              Integrations
            </CardTitle>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7"
              onClick={() => navigate('/superadmin/integrations')}
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => {
              const config = STATUS_CONFIG[category.status];
              const Icon = CATEGORY_ICONS[category.id] || Circle;
              
              return (
                <div
                  key={category.id}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full ${config.bg} ${config.border} border`}
                  title={`${category.name}: ${category.status_label}`}
                >
                  <Icon className={`h-3.5 w-3.5 ${config.color}`} />
                  <span className={`text-xs font-medium ${config.color}`}>
                    {category.name.split(' ')[0]}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full view
  return (
    <Card data-testid="integration-status-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <OverallIcon className={`h-5 w-5 ${STATUS_CONFIG[overallStatus].color}`} />
              Integration Status
            </CardTitle>
            <CardDescription className="mt-1">
              {overallStatus === 'green' && 'All enabled integrations are operational'}
              {overallStatus === 'yellow' && 'Some integrations need attention'}
              {overallStatus === 'red' && 'One or more integrations have errors'}
              {overallStatus === 'gray' && 'No integrations configured'}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-status-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3">
          {categories.map((category) => {
            const config = STATUS_CONFIG[category.status];
            const StatusIcon = config.icon;
            const CategoryIcon = CATEGORY_ICONS[category.id] || Circle;
            const enabledIntegrations = getEnabledIntegrationsForCategory(category.id);
            
            return (
              <div
                key={category.id}
                className={`flex items-center justify-between p-4 rounded-lg border ${config.border} ${config.bg} transition-all hover:shadow-sm cursor-pointer`}
                onClick={() => navigate('/superadmin/integrations')}
                data-testid={`status-${category.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-background/80`}>
                    <CategoryIcon className={`h-5 w-5 ${config.color}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{category.name}</span>
                      <Badge 
                        variant="secondary" 
                        className={`text-xs ${config.color} ${config.bg}`}
                      >
                        {category.status_label}
                      </Badge>
                      {/* Show small icons for enabled integrations */}
                      {enabledIntegrations.length > 0 && (
                        <div className="flex items-center gap-1 ml-2">
                          {enabledIntegrations.map((int) => (
                            <span 
                              key={int.integration_type}
                              className="inline-flex"
                              style={{ color: colors[int.integration_type] || '#666' }}
                              title={int.integration_type}
                            >
                              {icons[int.integration_type] && (
                                <span className="[&>svg]:h-4 [&>svg]:w-4">
                                  {icons[int.integration_type]}
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {category.description}
                    </p>
                    {category.last_error && category.status === 'red' && (
                      <p className="text-xs text-red-500 mt-1 line-clamp-1">
                        Error: {category.last_error}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">
                      {category.enabled_count}/{category.total_providers} enabled
                    </p>
                    {category.last_checked && (
                      <p className="text-xs text-muted-foreground">
                        Checked {formatLastChecked(category.last_checked)}
                      </p>
                    )}
                  </div>
                  <StatusIcon className={`h-5 w-5 ${config.color} ${config.pulse ? 'animate-pulse' : ''}`} />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
